import React, { useEffect, useState } from "react";
import { Card, Empty, Segmented, Space, Spin, Typography } from "antd";
import { getChartTheme } from "../../../../theme/chart-theme";
import { useThemeMode } from "../../../../theme/theme-provider";
import {
    Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { format } from "date-fns";
import logApi from "../../../../api/log-api";

const { Text } = Typography;

// Biểu đồ cột số dòng log theo mốc thời gian, dùng chung cho cả 3 tab.
// Mục đích: nhìn ra "lỗi dồn vào lúc nào" mà không phải lật từng trang bảng.
//
// type   - "errors" | "clicks" | "access"
// filter - bộ lọc hiện tại của tab (đồng bộ với bảng bên dưới)
// reloadKey - đổi giá trị này để ép vẽ lại (bấm Lọc / tải lại)
function LogTimelineChart({ type, filter, reloadKey, color = "#1677ff" }) {
    // recharts nhận màu qua prop, không ăn theo antd ConfigProvider
    const { isDark } = useThemeMode();
    const chart = getChartTheme(isDark);
    const [bucket, setBucket] = useState("hour");
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        logApi.getTimeline(type, { ...filter, bucket })
            .then(({ data: res }) => {
                if (!cancelled) setData(res.data || []);
            })
            .catch(() => {
                if (!cancelled) setData([]);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [type, bucket, reloadKey]);

    // Nhãn trục X: theo giờ thì chỉ cần giờ, theo ngày thì ngày/tháng.
    const fmtTick = (ts) =>
        format(new Date(ts * 1000), bucket === "hour" ? "dd/MM HH:mm" : "dd/MM");

    return (
        <Card
            size="small"
            className="shadow-sm"
            style={{ marginBottom: 16 }}
            title={<Text strong>Phân bố theo thời gian</Text>}
            extra={
                <Segmented
                    size="small"
                    value={bucket}
                    onChange={setBucket}
                    options={[
                        { value: "hour", label: "Theo giờ" },
                        { value: "day", label: "Theo ngày" },
                    ]}
                />
            }
        >
            {loading ? (
                <Space style={{ width: "100%", justifyContent: "center", padding: 32 }}>
                    <Spin />
                </Space>
            ) : !data.length ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Không có dữ liệu trong khoảng lọc" />
            ) : (
                <ResponsiveContainer width="100%" height={180}>
                    <BarChart data={data} margin={{ top: 4, right: 8, bottom: 0, left: -16 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={chart.grid} />
                        <XAxis dataKey="ts" tickFormatter={fmtTick} tick={{ fontSize: 11, fill: chart.axis }} stroke={chart.axis} minTickGap={24} />
                        <YAxis tick={{ fontSize: 11, fill: chart.axis }} stroke={chart.axis} allowDecimals={false} />
                        <Tooltip
                            labelFormatter={(ts) => fmtTick(ts)}
                            formatter={(v) => [Number(v).toLocaleString("vi-VN"), "Số dòng"]}
                        />
                        <Bar dataKey="count" fill={color} radius={[3, 3, 0, 0]} />
                    </BarChart>
                </ResponsiveContainer>
            )}
        </Card>
    );
}

export default LogTimelineChart;
